import React, { useEffect, useState } from "react";
import { ChartHeader } from "assets/css/global";
import { useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import AlertModal from "components/AlertModal";
import * as moment from "moment";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { palletMixPieChartApi } from "redux/actions/dashboard";
import MaterialToolTip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { formatCurrencyByCountry, getLoggedInUserCountry } from "helpers";

ChartJS.register(ArcElement, Tooltip, Legend);

const PieChart = (props) => {
    const { chartFilters } = props;
    const loggedInUserInfo = JSON.parse(localStorage.getItem('loggedInUserData'))
    const userCountry = getLoggedInUserCountry();
    const token = loggedInUserInfo?.token
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [palletMixApiData, setPalletMixApiData] = useState();
    const [pieChartDataset, setPieChartDataset] = useState([]);
    const [pieChartLabelset, setPieChartLabelset] = useState([]);
    const [modal, setModal] = useState(false);
    const [modalContent, setModalContent] = useState("");
    const [modalLink, setModalLink] = useState("");
    const toggleModal = () => setModal(!modal);

    useEffect(() => {
        if (chartFilters) fetchPalletMixData();
    }, [chartFilters]);

    const fetchPalletMixData = async () => {
        let data = {
            locationIds: chartFilters?.locationIdList,
            startDate: moment(chartFilters?.fromDate).format('YYYY-MM-DD'),
            endDate: moment(chartFilters?.toDate).format('YYYY-MM-DD'),
            country: userCountry
        };
        let response = await dispatch(palletMixPieChartApi(data, token));
        if (response.data) {
            let responseData = response.data?.sort((a, b) => b.numberOfOrder - a.numberOfOrder)
            setPalletMixApiData(responseData);
            setPieChartLabelset(responseData?.map(data => `${data.palletName}, ${data.numberOfOrder}, ${data.percentage}%`));
            setPieChartDataset(responseData?.map(data => data?.percentage));
        } else {
            if (response?.data?.error?.message === "Invalid Token") {
                navigate("/");
            } else {
                setModalContent(response?.data?.error?.message || response.message || props.locale('genericErrorLater'));
                setModalLink("");
                toggleModal();
            }
        }
    };

    const data = {
        labels: pieChartLabelset,
        datasets: [
            {
                data: pieChartDataset,
                borderColor: ['#4472c4', '#ed7d31', '#a5a5a5', '#ffc000', '#5b9bd5', '#70ad47', '#8d6e63', '#005586', '#27ae55', '#ffb400', '#c0ca33', '#aeca7d', '#cdd8dd', '#d3c59f', '#B0E0E6', '#feffa3'],
                backgroundColor: ['#4472c4', '#ed7d31', '#a5a5a5', '#ffc000', '#5b9bd5', '#70ad47', '#8d6e63', '#005586', '#27ae55', '#ffb400', '#c0ca33', '#aeca7d', '#cdd8dd', '#d3c59f', '#B0E0E6', '#feffa3'],
                borderWidth: 2,
                borderAlign: 'inner',
                offset: 10,
            },
        ],
    };

    const options = {
        plugins: {
            legend: {
                display: false,
            }
        },
    };

    const StyledTooltip = styled(({ className, ...props }) => (
        <MaterialToolTip {...props} classes={{ popper: className }} />
    ))(({ theme }) => ({
        zIndex: theme.zIndex.tooltip + 1,
        [`& .MuiTooltip-tooltip`]: {
            backgroundColor: '#FFFFFF',
            color: '#000000',
            border: '.5px solid #999999',
            fontSize: '.75rem',
            fontWeight: '400',
            cursor: 'pointer'
        }
    }));

    const getTitleUI = (palletMixApiData) => {
        return (
            <div className="tooltip-content">
                {palletMixApiData.slice(5).map((data, index) => (
                    <p key={index} style={{ fontSize: "13px", margin: "5px", }}>
                        <span
                            className="label-point"
                            style={{
                                backgroundColor:
                                    index < 9
                                        ? ['#70ad47', '#8d6e63', '#005586', '#27ae55', '#ffb400', '#c0ca33', '#aeca7d', '#cdd8dd', '#d3c59f', '#B0E0E6', '#feffa3'][index]
                                        : '#ed7d31',
                                width: "11px",
                                height: "11px",
                                borderRadius: "50%",
                                display: "inline-block",
                                marginRight: "5px",
                            }}
                        ></span>
                        {data.palletName}
                    </p>
                ))}
            </div>
        );
    };

    const displayLabels = () => {
        return (
            <div className="label-container">
                {palletMixApiData &&
                    palletMixApiData
                        ?.filter((data) => data.percentage > 0)
                        .slice(0, 5)
                        .map((data, index) => (
                            <p key={index}>
                                <span
                                    className="label-point"
                                    style={{
                                        backgroundColor:
                                            index < 5
                                                ? ['#4472c4', '#ed7d31', '#a5a5a5', '#ffc000', '#5b9bd5'][index]
                                                : '#ed7d31',
                                    }}
                                ></span>
                                {data.palletName}
                            </p>
                        ))}
                {palletMixApiData && palletMixApiData.length > 5 && (
                    <StyledTooltip
                        placement="top"
                        arrow
                        title={getTitleUI(palletMixApiData)}
                        enterTouchDelay={0}
                    >
                        <p style={{ color: "#283593", fontSize: "12px", fontWeight: "bold" }}>{props.locale('showMore')}</p>
                    </StyledTooltip>
                )}
            </div>
        );
    };

    const getTableTooltipUI = () => {
        return (
            <div>
                <table className="table table-light table-striped table-bordered">
                    <tbody>
                        {palletMixApiData.slice(5).map((data, index) => (
                            <tr>
                                <td>{data?.palletName}</td>
                                <td>{data?.numberOfOrder}</td>
                                <td>{formatCurrencyByCountry(data?.averageCost, userCountry)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="lineBarChartWrapper">
            <div>
                <ChartHeader>
                    <h4 style={{ fontSize: '13px' }}>{props.locale('palletMixTitle')}</h4>
                </ChartHeader>
                <div className="chart-wrapper" style={{ marginTop: '0px' }}>
                    {palletMixApiData && palletMixApiData.length > 0 ? (
                        <div className="" style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr' }}>
                            <div className="pie-chart-wrapper">
                                <Pie data={data} options={options} />
                                {displayLabels()}
                            </div>
                            <div className="table-container">
                                <div style={{ marginBottom: '15px' }}>
                                    <div className="table-row-header">
                                        <div className="volume-header">{props.locale('loadCount')}</div>
                                        <div>{props.locale('avgCost')}</div>
                                    </div>
                                    {palletMixApiData?.slice(0, 5).filter(data => data.percentage > 0)?.map((data, index, array) => (
                                        <div className={`table-row${index === array.length - 1 ? ' table-row-last' : ''}`} key={index}>
                                            <div className="table-cell table-cell-first">{data?.palletName}</div>
                                            <div className="table-cell table-cell-second">{data?.numberOfOrder}</div>
                                            <div className="table-cell table-cell-third">{formatCurrencyByCountry(data?.averageCost, userCountry)}</div>
                                        </div>
                                    ))}
                                    {palletMixApiData && palletMixApiData.length > 5 && (
                                        <StyledTooltip
                                            placement="left"
                                            arrow
                                            title={getTableTooltipUI()}
                                            enterTouchDelay={0}
                                        >
                                            <p style={{ color: "#283593", fontSize: "12px", margin: "2px 0px 10px 0px", fontWeight: "bold", width: "fit-content", float: "right" }}>{props.locale('showMore')}</p>
                                        </StyledTooltip>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : <p>{props.locale('noDataAvailable')}</p>}
                </div>
            </div>
            <div style={{ textAlign: 'end' }}>
                <h4 style={{ fontSize: '14px', fontFamily: 'latobold', fontStyle: 'italic', color: '#005586', cursor: 'pointer' }}>{props.locale('excludesTaxes')}</h4>
            </div>

            <AlertModal
                isOpen={modal}
                toggle={toggleModal}
                title={props.locale('alert')}
                content={modalContent}
                link={modalLink}
                className="alert-msg-container"
                InnerClassName="alert-msg"
            />
        </div>
    );
};

export default PieChart;
